import { useState, type FormEvent, useRef, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { Button, Alert } from '@/components/ui'

export function MFAPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { verifyMFA, isLoading, error, clearError, requiresMFA } = useAuth()

  const [code, setCode] = useState(['', '', '', '', '', ''])
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const from = location.state?.from || '/'

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  // Redirect if no MFA is required
  useEffect(() => {
    if (!requiresMFA) {
      navigate('/login', { replace: true })
    }
  }, [requiresMFA, navigate])

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const newCode = [...code]
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i]
    }
    setCode(newCode)
    // Focus last filled input or submit if complete
    const lastFilledIndex = Math.min(pastedData.length - 1, 5)
    inputRefs.current[lastFilledIndex]?.focus()
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    clearError()

    const fullCode = code.join('')
    if (fullCode.length !== 6) return

    try {
      await verifyMFA(fullCode)
      navigate(from, { replace: true })
    } catch {
      // Error is handled by context
      // Reset code on error
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    }
  }

  // Auto-submit when code is complete
  useEffect(() => {
    const fullCode = code.join('')
    if (fullCode.length === 6 && !isLoading) {
      handleSubmit({ preventDefault: () => {} } as FormEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)] px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-[var(--accent)] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">u</span>
            </div>
            <span className="font-semibold text-xl text-[var(--text-primary)]">
              u.software
            </span>
          </div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">
            Two-factor authentication
          </h1>
          <p className="mt-2 text-[var(--text-secondary)]">
            Enter the code from your authenticator app
          </p>
        </div>

        {/* Form */}
        <div className="bg-[var(--bg-elevated)] rounded-xl border border-[var(--border-default)] p-6">
          {error && (
            <Alert variant="error" className="mb-4" onClose={clearError}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Code inputs */}
            <div className="flex justify-center gap-2">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-14 text-center text-2xl font-semibold border border-[var(--border-default)] rounded-lg bg-[var(--bg-base)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] transition-colors"
                  disabled={isLoading}
                />
              ))}
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={isLoading}
              disabled={code.join('').length !== 6}
            >
              Verify
            </Button>
          </form>

          {/* Alternative options */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-sm text-[var(--text-muted)]">
              Having trouble?
            </p>
            <div className="flex justify-center gap-4">
              <button
                type="button"
                className="text-sm text-[var(--accent)] hover:underline"
              >
                Use recovery code
              </button>
              <span className="text-[var(--border-default)]">|</span>
              <button
                type="button"
                className="text-sm text-[var(--accent)] hover:underline"
              >
                Try another method
              </button>
            </div>
          </div>
        </div>

        {/* Back to login */}
        <p className="mt-6 text-center text-sm text-[var(--text-secondary)]">
          <Link
            to="/login"
            className="font-medium text-[var(--accent)] hover:underline"
          >
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
